import React, { useMemo } from 'react';
import { Flex, Segmented, Typography, theme } from 'antd';
import { AppstoreOutlined, BarsOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CalculatorData } from './Calculator';
import { computeUnitEconomics } from '../../shared/lib/unitEconomics';
import { useAppColors } from '../../shared/theme';
import HintLabel from '../../shared/ui/HintLabel';
import type { DashboardViewMode, PageSection } from '../../shared/lib/dashboardView';

interface ResultBarProps {
  data?: CalculatorData;
  launchBudget: number;
  /** Блоки страницы: в ленте — якоря прокрутки, во вкладках — сами вкладки. */
  sections: readonly PageSection[];
  mode: DashboardViewMode;
  onModeChange: (mode: DashboardViewMode) => void;
  /** Открытая вкладка (вид «вкладки»). */
  activeTab: string;
  onSelectTab: (id: string) => void;
  /**
   * Числа не подтверждены данными товара (Keepa его не знает или ещё грузит): плитки
   * нейтральные — зелёная шапка выдавала расчёт на прежних числах за настоящий
   * (QA 09.10, WS 15587410).
   */
  unverified?: boolean;
}

const formatMoney = (v: number) => (v < 0 ? '−$' : '$') + Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 });

/**
 * Липкая полоса страницы расчёта (раскладка «вариант 4», фидбек 29.09): итог
 * всегда на виду, пока листаешь свёрнутые блоки, плюс переход к любому блоку.
 *
 * Своя, а не шапки: в embed шапку рисует платформа, и без полосы переходов по
 * блокам там не было бы вовсе. Числа — те же, что в «Сводке» («с учётом всех
 * затрат, без бюджета на запуск»), из того же `computeUnitEconomics`.
 */
const ResultBar: React.FC<ResultBarProps> = ({ data, launchBudget, sections, mode, onModeChange, activeTab, onSelectTab, unverified = false }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const c = useAppColors();
  const econ = useMemo(
    () => (data ? computeUnitEconomics(data, launchBudget, data.couponPromoEnabled ?? false) : null),
    [data, launchBudget],
  );

  const tiles = econ
    ? [
        { key: 'profit', label: t('idea_calc.result_bar.profit_unit'), hint: t('idea_calc.result_bar.tips.profit_unit'), value: formatMoney(econ.profitPerUnitWithCosts), ok: econ.profitPerUnitWithCosts >= 0 },
        { key: 'margin', label: t('idea_calc.result_bar.margin'), hint: t('idea_calc.result_bar.tips.margin'), value: `${econ.marginPerUnitWithCosts.toFixed(1)}%`, ok: econ.marginPerUnitWithCosts >= 0 },
        { key: 'roi', label: t('idea_calc.result_bar.roi'), hint: t('idea_calc.result_bar.tips.roi'), value: `${econ.roiPerUnitWithCosts.toFixed(0)}%`, ok: econ.roiPerUnitWithCosts >= 0 },
        { key: 'batch', label: t('idea_calc.result_bar.batch_profit'), hint: t('idea_calc.result_bar.tips.batch_profit'), value: formatMoney(econ.batchProfit), ok: econ.batchProfit >= 0 },
      ]
    : [];

  // Лента — прокрутка к блоку; вкладки — открыть блок (как в софте Игоря).
  const jump = (id: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    if (mode === 'tabs') onSelectTab(id);
    else document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      data-testid="result-bar"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: `${token.paddingSM}px ${token.padding}px`,
        boxShadow: token.boxShadowTertiary,
      }}
    >
      <Flex align="center" gap="middle" wrap>
        {tiles.map((tile) => (
          <div
            key={tile.key}
            style={{
              padding: `${token.paddingXXS}px ${token.paddingSM}px`,
              borderRadius: token.borderRadius,
              background: unverified ? c.fillMuted : tile.ok ? c.successBg : c.errorBg,
              minWidth: 120,
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}><HintLabel hint={tile.hint}>{tile.label}</HintLabel></Typography.Text>
            <Typography.Text strong style={{ fontSize: token.fontSizeHeading4, color: unverified ? c.textSecondary : tile.ok ? c.successText : c.errorText }}>{tile.value}</Typography.Text>
          </div>
        ))}
        <div style={{ flex: '1 1 auto' }} />
        <Segmented<DashboardViewMode>
          data-testid="view-mode"
          value={mode}
          onChange={onModeChange}
          options={[
            { value: 'feed', label: t('idea_calc.result_bar.view_feed'), icon: <BarsOutlined /> },
            { value: 'tabs', label: t('idea_calc.result_bar.view_tabs'), icon: <AppstoreOutlined /> },
          ]}
        />
      </Flex>
      <nav aria-label={t('idea_calc.result_bar.nav_label')} style={{ marginTop: token.marginSM }}>
        <Flex gap="small" wrap role={mode === 'tabs' ? 'tablist' : undefined}>
          {sections.map((s) => {
            const active = mode === 'tabs' && activeTab === s.id;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={jump(s.id)}
                role={mode === 'tabs' ? 'tab' : undefined}
                aria-selected={mode === 'tabs' ? active : undefined}
                style={{
                  padding: `${token.paddingXXS}px ${token.paddingSM}px`,
                  borderRadius: 999,
                  border: `1px solid ${active ? token.colorPrimary : token.colorBorder}`,
                  background: active ? token.colorPrimary : token.colorFillQuaternary,
                  color: active ? token.colorTextLightSolid : token.colorText,
                  fontWeight: active ? 600 : undefined,
                  textDecoration: 'none',
                  fontSize: token.fontSize,
                }}
              >
                {t(s.titleKey)}
              </a>
            );
          })}
        </Flex>
      </nav>
    </div>
  );
};

export default ResultBar;
